import { createContext, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// Lets a page publish its title + actions into the global top bar. The top bar
// renders <PageChromeTarget/> as the slot; a page's header portals into it via
// <PageChromePortal/>. `undefined` = no provider in the tree (tests/Storybook),
// where portals fall back to rendering inline; `null` = provider mounted but the
// slot element hasn't attached yet (one commit before the ref fires).
const TargetContext = createContext<HTMLElement | null | undefined>(undefined);
const SetTargetContext = createContext<((el: HTMLElement | null) => void) | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  return (
    <SetTargetContext.Provider value={setTarget}>
      <TargetContext.Provider value={target}>{children}</TargetContext.Provider>
    </SetTargetContext.Provider>
  );
}

// The top bar's slot: pages portal their chrome here. Empty until one publishes.
export function PageChromeTarget({ className }: { className?: string }) {
  const setTarget = useContext(SetTargetContext);
  return <div ref={setTarget ?? undefined} className={className} />;
}

export function PageChromePortal({ children }: { children: ReactNode }) {
  const target = useContext(TargetContext);
  if (target === undefined) return <>{children}</>;
  if (target === null) return null;
  return createPortal(children, target);
}
